import React, { useState } from "react";
import {
  PlusOutlined,
  EditOutlined,
  DeleteOutlined,
  EyeOutlined,
  ShoppingCartOutlined,
} from "@ant-design/icons";
import { CustomDataTable } from "@/components/UI/Tables/Datatable";
import { useEventData } from "../hooks/useEventData";
import "./EventTable.css";

/**
 * Event Table Component
 * Uses the reusable Datatable component without tabs
 */
const EventTable = ({
  onAddEvent,
  onEditEvent,
  onViewEvent,
  onAddProducts,
}) => {
  const [selectedRowKeys, setSelectedRowKeys] = useState([]);

  // Use custom hook for event data with pagination and search
  const {
    eventsData,
    paginationInfo,
    handlePageChange,
    handlePageSizeChange,
    search,
    handleSearch,
    isLoading,
    isFetching,
    error,
  } = useEventData();

  // Define columns for catering package data
  const eventColumns = [
    {
      title: "Package Name",
      dataIndex: "name",
      key: "name",
      width: 300,
      render: (text, record) => (
        // <div className="flex items-center gap-3">
        //   <div className="relative">
        //     <div className="w-10 h-10 rounded-full bg-gray-100 border border-gray-200 flex items-center justify-center">
        //       <span className="text-gray-400 text-xs">🍽️</span>
        //     </div>
        //   </div>
        //   <div className="flex flex-col">
        //     <span className="font-medium text-gray-900">{text}</span>
        //     <span className="text-sm text-gray-500">PKG{record.id}</span>
        //   </div>
        // </div>

        <div className="flex items-center gap-3">
          <div className="relative">
            {record.url !== "" && record.url!==null? (
              <img
                src={record.url}
                alt={text}
                className="w-10 h-10 rounded-full object-cover border border-gray-200"
                onError={(e) => {
                  e.target.style.display = "none";
                  e.target.nextSibling.style.display = "flex";
                }}
              />
            ) : (
              <div
                className="w-10 h-10 rounded-full bg-gray-100 border border-gray-200 flex items-center justify-center"
                style={{ display: record.image ? "none" : "flex" }}
              >
                <span className="text-gray-400 text-xs">🍽️</span>
              </div>
            )}
          </div>
          <div className="flex flex-col">
            <span className="font-medium text-gray-900">{text}</span>
            <span className="text-sm text-gray-500">SFS{record.id}</span>
          </div>
        </div>
      ),
    },
    {
      title: "Min Serving",
      dataIndex: "minimum_serving_size",
      key: "minimum_serving_size",
      width: 120,
      render: (size) => (
        <span className="font-medium text-gray-900">{size || 0}</span>
      ),
    },
    {
      title: "Max Serving",
      dataIndex: "maximum_serving_size",
      key: "maximum_serving_size",
      width: 120,
      render: (size) => (
        <span className="font-medium text-gray-900">{size || 0}</span>
      ),
    },
    {
      title: "Status",
      dataIndex: "status",
      key: "status",
      width: 120,
      render: (status) => (
        <span
          className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${
            status === "Active"
              ? "bg-green-100 text-green-800"
              : "bg-gray-100 text-gray-800"
          }`}
        >
          {status || "Active"}
        </span>
      ),
    },
  ];

  // Action buttons for the table
  const actionButtons = [
    {
      label: "Add Package",
      type: "primary",
      icon: <PlusOutlined />,
      onClick: onAddEvent,
      className: "bg-blue-500 border-blue-500 hover:bg-blue-600",
    },
  ];

  // Row selection configuration
  const rowSelection = {
    selectedRowKeys,
    onChange: (keys) => setSelectedRowKeys(keys),
  };

  // Row actions for catering package table
  const rowActions = [
    {
      label: "View Details",
      icon: <EyeOutlined />,
      onClick: (record) => onViewEvent && onViewEvent(record),
    },
    {
      label: "Edit Package",
      icon: <EditOutlined />,
      onClick: (record) => onEditEvent && onEditEvent(record),
    },
    {
      label: "Add Products",
      icon: <ShoppingCartOutlined />,
      onClick: (record) => onAddProducts && onAddProducts(record),
    },
  ];

  // Handle bulk actions
  const handleBulkDelete = (selectedKeys) => {
    // console.log("Bulk delete packages:", selectedKeys);
    // Implement bulk delete logic here
  };

  const handleBulkEdit = (selectedKeys) => {
    // console.log("Bulk edit packages:", selectedKeys);
    // Implement bulk edit logic here
  };

  // Handle loading and error states
  if (error) {
    return (
      <div className="flex justify-center items-center h-64">
        <div className="text-lg text-red-600">
          Error loading packages: {error?.message || "Unknown error"}
        </div>
      </div>
    );
  }

  return (
    <CustomDataTable
      title="Catering Packages"
      data={eventsData}
      columns={eventColumns}
      showSearch={true}
      searchPlaceholder="Search packages..."
      searchValue={search}
      onServerSearch={handleSearch}
      searchMode="server"
      actionButtons={actionButtons}
      showFilter={false}
      showCSV={true}
      paginationMode="server"
      currentPage={paginationInfo.current_page}
      totalItems={paginationInfo.total}
      serverPageSize={paginationInfo.per_page}
      onPageChange={handlePageChange}
      onPageSizeChange={handlePageSizeChange}
      rowSelection={rowSelection}
      selectedRowKeys={selectedRowKeys}
      rowActions={rowActions}
      loading={isLoading || isFetching}
      footerInfo={{
        left: `${selectedRowKeys.length} of ${eventsData.length} row(s) selected.`,
      }}
      tableClassName="catering-package-table"
    />
  );
};

export default EventTable;
